import React, { useEffect, useMemo, useState } from 'react';
import AvatarPanel from './AvatarPanel.jsx';
import { AVATAR_STATES, DEFAULT_AVATAR_REGISTRY } from './avatarRegistry.js';
import { resolveAvatarsForModule } from './avatarResolver.js';
import {
  DOCKS,
  loadAvatarPreferences,
  saveAvatarPreferences,
} from './avatarPreferences.js';
import { useReducedMotion } from './useReducedMotion.js';
import './AvatarOverlayLayer.css';

const normalizeState = (value) => {
  const requested = typeof value === 'string' ? value : value?.state;
  return AVATAR_STATES.includes(requested) ? requested : 'idle';
};

/**
 * Application-level N-avatar overlay. Mount once inside the authenticated app
 * shell, after the router, so moduleId/pathname can be supplied by the shell.
 */
export function AvatarOverlayLayer({
  moduleId,
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
  registry = DEFAULT_AVATAR_REGISTRY,
  assignments = [],
  summons = [],
  stateByAvatar = {},
  activeSpeakerId = null,
  disabled = false,
  onAsk,
  onVoiceToggle,
  onMuteChange,
  onPreferenceChange,
}) {
  const reducedMotion = useReducedMotion();
  const [preferences, setPreferences] = useState(loadAvatarPreferences);

  useEffect(() => {
    saveAvatarPreferences(preferences);
    onPreferenceChange?.(preferences);
  }, [onPreferenceChange, preferences]);

  const contextAvatars = useMemo(() => resolveAvatarsForModule({
    moduleId,
    pathname,
    registry,
    assignments,
    summons,
  }), [assignments, moduleId, pathname, registry, summons]);

  const updatePreference = (group, id, value) => {
    setPreferences((current) => ({
      ...current,
      [group]: { ...current[group], [id]: value },
    }));
  };

  if (disabled || contextAvatars.length === 0) return null;

  const visible = contextAvatars.filter((avatar) => !preferences.hidden[avatar.id]);
  const hidden = contextAvatars.filter((avatar) => preferences.hidden[avatar.id]);
  const minimized = visible.filter((avatar) => preferences.minimized[avatar.id]);
  const expanded = visible.filter((avatar) => !preferences.minimized[avatar.id]);

  return (
    <aside className="cl-avatar-layer" aria-label="CommandLinc assistants">
      <div className="cl-avatar-layer__expanded">
        {DOCKS.map((dock) => {
          const docked = expanded.filter((avatar) => (
            preferences.docks[avatar.id] || avatar.defaultDock || 'bottom-right'
          ) === dock);
          if (docked.length === 0) return null;
          return (
            <div className={`cl-avatar-dock cl-avatar-dock--${dock}`} key={dock}>
              {docked.map((avatar) => {
                const state = normalizeState(stateByAvatar[avatar.id]);
                return (
                  <AvatarPanel
                    key={avatar.id}
                    avatar={avatar}
                    state={state}
                    dock={dock}
                    position={preferences.positions[avatar.id] || { x: 0, y: 0 }}
                    muted={Boolean(preferences.muted[avatar.id])}
                    activeSpeaker={activeSpeakerId === avatar.id || state === 'speaking'}
                    reducedMotion={reducedMotion}
                    onAsk={onAsk}
                    onVoiceToggle={onVoiceToggle}
                    onMuteToggle={(id) => {
                      const muted = !preferences.muted[id];
                      updatePreference('muted', id, muted);
                      onMuteChange?.(id, muted);
                    }}
                    onMinimize={(id) => updatePreference('minimized', id, true)}
                    onHide={(id) => updatePreference('hidden', id, true)}
                    onPositionChange={(id, nextPosition) => updatePreference('positions', id, nextPosition)}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {(minimized.length > 0 || hidden.length > 0) && (
        <div className="cl-avatar-tray" aria-label="Assistant visibility controls">
          {minimized.map((avatar) => (
            <button
              type="button"
              key={avatar.id}
              style={{ '--avatar-accent': avatar.accent || '#31d7ff' }}
              onClick={() => updatePreference('minimized', avatar.id, false)}
              aria-label={`Restore ${avatar.name}`}
            >
              <span>{avatar.name.slice(0, 1)}</span>{avatar.name}
            </button>
          ))}
          {hidden.map((avatar) => (
            <button
              type="button"
              className="is-hidden"
              key={avatar.id}
              onClick={() => updatePreference('hidden', avatar.id, false)}
              aria-label={`Show ${avatar.name}`}
            >
              Show {avatar.name}
            </button>
          ))}
        </div>
      )}
    </aside>
  );
}

export default AvatarOverlayLayer;
